import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard, Building2, MapPin, Users, Globe2, Briefcase, Swords, Gem, Star, CalendarRange, Palette, Megaphone, Rocket,
  Store, FileText, KanbanSquare, CalendarCheck, Boxes, BadgeIndianRupee, Handshake, Gift, Calculator, BarChart3, Lightbulb,
  UserCog, CreditCard, Bell, ScrollText, ShieldCheck, Menu, LogOut, Coins, Presentation, Link2, CalendarClock, ArrowLeftRight, Check,
} from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { accountTypeOf, accountTypeLabel } from "@/lib/accountTypes";
import { useAuth } from "@/context/AuthContext";
import { api, num } from "@/lib/api";
import Copilot from "@/components/Copilot";

export const NAV = [
  { group: "Command", items: [["Dashboard", "/dashboard", LayoutDashboard, "analytics"], ["AI Daily Advisor", "/advisor", Lightbulb, "intelligence"]] },
  { group: "Plan", items: [["Projects", "/projects", Building2, "projects"], ["Project Intelligence", "/intelligence/project", MapPin, "intelligence"],
    ["Buyer Intelligence", "/intelligence/buyer", Users, "intelligence"], ["Community Intelligence", "/intelligence/community", Globe2, "intelligence"],
    ["Corporate Intelligence", "/intelligence/corporate", Briefcase, "intelligence"], ["Competitor Intelligence", "/intelligence/competitor", Swords, "intelligence"]] },
  { group: "Market", items: [["Brand Positioning", "/brand/positioning", Gem, "intelligence"], ["Brand Ambassadors", "/brand/ambassadors", Star, "intelligence"],
    ["Marketing Planner", "/marketing/planner", CalendarRange, "intelligence"], ["Creative Studio", "/creative-studio", Palette, "marketing"], ["Festival Calendar", "/festivals", CalendarRange, "marketing"], ["Content Calendar", "/content-calendar", CalendarClock, "marketing"], ["Campaign Center", "/campaigns", Megaphone, "marketing"], ["Manage Meta Campaigns", "/ads-manager", Rocket, "marketing"]] },
  { group: "Launch", items: [["Launch My Project Marketing", "/launch-wizard", Rocket, "marketing"], ["Launch Orchestration", "/launch", Rocket, "intelligence"], ["Vendor Marketplace", "/vendors", Store, "marketing"], ["RFQ Center", "/rfqs", FileText, "marketing"]] },
  { group: "Sell", items: [["Leads", "/leads", KanbanSquare, "sales"], ["Site Visits", "/site-visits", CalendarCheck, "sales"], ["Inventory", "/inventory", Boxes, "sales"],
    ["Bookings", "/bookings", BadgeIndianRupee, "sales"], ["Channel Partners", "/channel-partners", Handshake, "sales"], ["Referrals", "/referrals", Gift, "sales"]] },
  { group: "Grow", items: [["Cost of Selling", "/cost-of-selling", Calculator, "finance"], ["Analytics Center", "/analytics", BarChart3, "analytics"], ["Boardroom Decks", "/presentations", Presentation, "analytics"]] },
  { group: "Organization", items: [["Team & Roles", "/team", UserCog, "projects"], ["Billing & AI Credits", "/billing", CreditCard, "billing"], ["Branding", "/branding", Palette, "billing"], ["Shared Links", "/shares", Link2, "projects"], ["Notifications", "/notifications", Bell, "projects"], ["Audit Logs", "/audit-logs", ScrollText, "team_write"]] },
];

function SideNav({ onNav }) {
  const { can, me } = useAuth();
  return (
    <div className="h-full flex flex-col">
      <Link to="/dashboard" className="px-4 h-16 flex items-center border-b border-slate-800 shrink-0" data-testid="sidebar-logo">
        <img src="/logo.webp" alt="LaunchX360 - Plan | Market | Launch | Sell | Grow" className="h-11 w-auto object-contain" />
      </Link>
      <nav className="flex-1 overflow-y-auto custom-scrollbar py-4 space-y-5">
        {me?.is_super_admin && (
          <NavLink to="/admin" onClick={onNav} data-testid="sidebar-admin-link" className={({ isActive }) => `mx-3 flex items-center gap-3 px-3 py-2 rounded-lg text-sm border ${isActive ? "border-cyan-500/40 bg-cyan-500/10 text-cyan-300" : "border-slate-800 text-cyan-400 hover:bg-slate-800/60"}`}>
            <ShieldCheck className="h-4 w-4" />Super Admin Portal
          </NavLink>
        )}
        {me?.agent && (
          <NavLink to="/agent/companies" onClick={onNav} data-testid="sidebar-agent-companies-link" className={({ isActive }) => `mx-3 flex items-center gap-3 px-3 py-2 rounded-lg text-sm border ${isActive ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600" : "border-slate-800 text-emerald-600 hover:bg-slate-800/60"}`}>
            <Building2 className="h-4 w-4" />My Developer Companies
          </NavLink>
        )}
        {NAV.map((g) => {
          const items = g.items.filter(([, , , m]) => (m === "team_write" ? can("team", "write") : can(m)));
          if (!items.length) return null;
          return (
            <div key={g.group}>
              <div className="px-5 mb-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-slate-600">{g.group}</div>
              {items.map(([label, to, Icon]) => (
                <NavLink key={to} to={to} onClick={onNav} data-testid={`sidebar-${to.replace(/\//g, "-").slice(1)}-link`}
                  className={({ isActive }) => `flex items-center gap-3 px-5 py-2 text-[13px] transition-colors ${isActive ? "bg-emerald-500/10 text-emerald-400 font-semibold border-r-2 border-emerald-500" : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/40"}`}>
                  <Icon className="h-4 w-4 shrink-0" />{label}
                </NavLink>
              ))}
            </div>
          );
        })}
      </nav>
      <div className="px-5 py-4 border-t border-slate-800"><Link to="/privacy-policy" onClick={onNav} className="text-[11px] text-slate-500 hover:text-emerald-700" data-testid="sidebar-privacy-link">Privacy Policy</Link><span className="text-slate-600 mx-1">·</span><Link to="/data-deletion" onClick={onNav} className="text-[11px] text-slate-500 hover:text-emerald-700" data-testid="sidebar-data-deletion-link">Data Deletion</Link></div>
    </div>
  );
}

function CompanySwitcher() {
  const { me } = useAuth();
  const nav = useNavigate();
  if (!me.agent) return null;
  const pick = (id) => {
    if (id === me.agent.home_organization_id) localStorage.removeItem("lx_org"); else localStorage.setItem("lx_org", id);
    window.location.assign("/dashboard");
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-emerald-500/40 text-emerald-700 text-xs font-semibold hover:bg-emerald-500/10 whitespace-nowrap" data-testid="agent-company-switcher">
        <ArrowLeftRight className="h-3.5 w-3.5" /><span className="hidden sm:inline">Switch company</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="bg-white border-slate-800 max-h-80 overflow-y-auto">
        <DropdownMenuLabel className="text-xs">Companies you manage</DropdownMenuLabel>
        {me.agent.companies.map((c) => (
          <DropdownMenuItem key={c.id} onClick={() => pick(c.id)} data-testid={`agent-switch-${c.id}`} className="flex justify-between gap-4">
            <span>{c.name}{c.is_agency ? " (my agency)" : ""}</span>{c.id === me.organization?.id && <Check className="h-3.5 w-3.5 text-emerald-600" />}
          </DropdownMenuItem>))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => nav("/agent/companies")} data-testid="agent-manage-companies">Add or manage companies</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Header({ onMenu }) {
  const { me, logout } = useAuth();
  const nav = useNavigate();
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    const f = () => api.get("/notifications").then((r) => setUnread(r.data.filter((n) => !n.read).length)).catch(() => {});
    f();
    const t = setInterval(f, 60000);
    return () => clearInterval(t);
  }, []);
  const sub = me.subscription;
  return (
    <header className="sticky top-0 z-30 h-16 bg-white/85 backdrop-blur-xl border-b border-slate-800/80 px-4 sm:px-6 flex items-center justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <button className="lg:hidden p-2 rounded-lg hover:bg-slate-800" onClick={onMenu} data-testid="mobile-menu-button"><Menu className="h-5 w-5" /></button>
        <div className="min-w-0">
          <div className="text-sm font-semibold truncate" data-testid="header-org-name">{me.organization?.name}</div>
          {sub && <div className="text-[11px] text-slate-500 font-mono truncate">{sub.plan_name} · {sub.status} · until {sub.end_date?.slice(0, 10)}</div>}
        </div>
        {me.organization?.is_demo && <span className="hidden sm:inline-flex"><span className="lx-mock-badge whitespace-nowrap" data-testid="demo-mode-badge">DEMO · SIMULATED</span></span>}
        {!me.is_super_admin && <Link to="/account" className="hidden sm:inline-flex px-2.5 py-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-700 text-[11px] font-semibold whitespace-nowrap hover:bg-emerald-500/20" data-testid="header-account-type-badge">{accountTypeLabel(accountTypeOf(me.user))}</Link>}
        <CompanySwitcher />
      </div>
      <div className="flex items-center gap-2 sm:gap-3 shrink-0">
        <Link to="/billing" data-testid="header-credit-balance" className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-cyan-500/30 bg-cyan-500/10 text-cyan-300 text-xs font-mono hover:bg-cyan-500/20 transition-colors">
          <Coins className="h-3.5 w-3.5" />{num(me.wallet?.balance)}<span className="hidden sm:inline"> credits</span>
        </Link>
        <Link to="/notifications" className="relative p-2 rounded-lg hover:bg-slate-800" data-testid="header-notifications-button">
          <Bell className="h-5 w-5 text-slate-300" />
          {unread > 0 && <span className="absolute top-1 right-1 h-4 min-w-4 px-1 rounded-full bg-rose-500 text-[10px] font-bold grid place-items-center" data-testid="header-unread-count">{unread}</span>}
        </Link>
        <DropdownMenu>
          <DropdownMenuTrigger className="flex items-center gap-2 rounded-full pl-1 pr-3 py-1 hover:bg-slate-800" data-testid="header-user-menu">
            <div className="h-8 w-8 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 grid place-items-center text-xs font-bold text-white">{me.user.name?.slice(0, 2).toUpperCase()}</div>
            <div className="hidden md:block text-left"><div className="text-xs font-semibold">{me.user.name}</div><div className="text-[10px] text-slate-500 font-mono">{me.user.role}</div></div>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-white border-slate-800">
            <DropdownMenuLabel className="text-xs">{me.user.email}{!me.is_super_admin && <span className="block text-emerald-600 font-semibold mt-0.5" data-testid="menu-account-type">{accountTypeLabel(accountTypeOf(me.user))}</span>}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {!me.is_super_admin && <DropdownMenuItem onClick={() => nav("/account")} data-testid="menu-account-settings">Account Settings</DropdownMenuItem>}
            <DropdownMenuItem onClick={() => nav("/team")}>Team & Roles</DropdownMenuItem>
            <DropdownMenuItem onClick={() => nav("/billing")}>Billing & Credits</DropdownMenuItem>
            <DropdownMenuItem onClick={async () => { await logout(); nav("/login"); }} data-testid="logout-button"><LogOut className="h-4 w-4 mr-2" />Logout</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

export default function Layout() {
  const [mobile, setMobile] = useState(false);
  const { me } = useAuth();
  return (
    <div className="min-h-screen bg-[#F6F7F9] text-slate-100">
      <aside className="hidden lg:block fixed left-0 top-0 bottom-0 z-40 w-64 bg-white backdrop-blur-md border-r border-slate-800"><SideNav /></aside>
      <Sheet open={mobile} onOpenChange={setMobile}>
        <SheetContent side="left" className="p-0 w-72 bg-white border-slate-800"><SideNav onNav={() => setMobile(false)} /></SheetContent>
      </Sheet>
      <div className="lg:pl-64">
        <Header onMenu={() => setMobile(true)} />
        <main className="px-4 sm:px-6 lg:px-10 py-8 max-w-[1600px]">
          {me?.organization?.is_demo && <div className="mb-6 rounded-lg border border-amber-500/40 bg-amber-50 px-4 py-2 text-xs" style={{ color: "#92400E" }} data-testid="demo-workspace-banner">
            <b>Demo workspace</b> · sample data for exploring LaunchX360. Real payments, live campaigns, publishing and external transactions are disabled.</div>}
          <Outlet />
        </main>
      </div>
      <Copilot />
    </div>
  );
}
